import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import { ActiveItemsService } from '../active-items/active-items.service';
import { InventoryItemWithPricing } from '../common/inventory-item';
import { InventoryListItem } from '../common/item-list/type';
import { ItemRow } from '../common/item-pricing-table-base';
import { CreateLineItemFromItemRow, LineItemPricingEqual } from '../common/item-pricing-table-base/line-item';
import { ItemDependencyService } from '../services/item-dependency.service';
import { DurationInterface } from '@vendasta/sales-orders/';

export type ChangedInventoryItem = InventoryItemWithPricing & { index: number };

export interface BillingTermCallbackOutput {
  startDate: Date;
  endDate?: Date;
  addAnotherTerm?: boolean;
  nonBillable?: boolean;
  duration?: DurationInterface;
}

export type BillingTermCallbackFn = (editing?: boolean) => Observable<BillingTermCallbackOutput>;

export interface ItemsChangedEvent {
  items: ItemRow[];
  type: ItemsChangedEventType;
  source: 'user' | 'dependency';
}

export type ItemsChangedEventType = 'add' | 'update' | 'overwrite' | 'delete';
export type ItemChangedEventSource = 'user' | 'dependency';

type ServiceConfig = {
  partnerId: string;
  marketId: string;
  businessId: string;
  enforceDependencies: boolean;
  currencyOverride: string;
};

@Injectable()
export class ItemPricingTableService {
  private _config = signal<ServiceConfig>({
    partnerId: '',
    marketId: '',
    businessId: '',
    currencyOverride: '',
    enforceDependencies: false,
  });

  private _items: WritableSignal<ItemRow[]> = signal<ItemRow[]>([]);
  items: Signal<ItemRow[]> = this._items.asReadonly();
  private _events: WritableSignal<ItemsChangedEvent[]> = signal<ItemsChangedEvent[]>([]);
  events: Signal<ItemsChangedEvent[]> = this._events.asReadonly();

  latestEvent = computed(() => {
    const events = this.events();
    const latestEvent = events?.length ? events[events.length - 1] : null;
    if (latestEvent) {
      this.eventHandler(latestEvent);
    }
    return latestEvent;
  });
  activeItems: Signal<InventoryListItem[]>;

  constructor(
    private activeItemsService: ActiveItemsService,
    private dependencyService: ItemDependencyService,
  ) {
    const config$ = toObservable(this._config);
    const activeItems$ = config$.pipe(
      filter((config) => Boolean(config.partnerId && config.marketId && config.businessId)),
      switchMap((config) => {
        return this.activeItemsService.getCurrentlyActiveItems(config.partnerId, config.businessId, config.marketId);
      }),
      startWith([]),
      shareReplay(1),
    );
    this.activeItems = toSignal(activeItems$);
  }

  setConfig(config: ServiceConfig): void {
    this._config.set(config);
  }

  initItems(items: ItemRow[]): void {
    if (!items?.length) {
      return;
    }

    this._items.set(items);
  }

  overwriteItems(items: ItemRow[]): void {
    const changedItems = items.map((item, index) => ({ ...item, index }));
    this.updateItems(() => items, changedItems, 'overwrite');
  }

  private eventHandler(event: ItemsChangedEvent): void {
    // Prevent chain reaction of dependency events
    if (event.source === 'dependency') return;

    if (event.type === 'delete' && this._config().enforceDependencies) {
      this.removeItemsWithMissingDependencies(this.items());
    }
  }

  addItems(newItems: ItemRow[]): void {
    if (!newItems?.length) {
      return;
    }
    let lastItemIndex = this._items().length - 1;
    const changedItems = newItems.map((item) => {
      lastItemIndex += 1;
      return { ...item, index: lastItemIndex };
    });
    this.updateItems((items) => items.concat(newItems), changedItems, 'add');
  }

  addItemAtIndex(newItem: ItemRow, index: number): void {
    const nItems = this._items().length;
    if (!this.isItemIndexValid(index) && index !== nItems) {
      return;
    }
    this.updateItems(
      (items) => {
        items.splice(index, 0, newItem);
        return items;
      },
      [{ ...newItem, index }],
      'add',
    );
  }

  setItemMatchingRow(itemRow: ItemRow): void {
    const index = itemRow.index ?? -1;
    if (!this.isItemIndexValid(index)) {
      return;
    }

    this.updateItems(
      (items) => {
        items[index] = itemRow;
        return items;
      },
      [{ ...itemRow, index }],
      'update',
    );
  }

  setItemsMatchingRows(itemRows: ItemRow[]): void {
    if (!itemRows?.length) {
      return;
    }

    this.updateItems(
      (items) => {
        itemRows.forEach((itemRow) => {
          const index = itemRow.index ?? -1;
          if (!this.isItemIndexValid(index)) {
            return;
          }
          items[index] = itemRow;
        });
        return items;
      },
      itemRows.map((itemRow) => {
        return {
          ...itemRow,
          pricing: CreateLineItemFromItemRow(itemRow, this._config().currencyOverride),
          index: itemRow.index ?? -1,
          quantity: itemRow.quantity,
        };
      }),
      'update',
    );
  }

  removeItemMatchingRow(itemRow: ItemRow): void {
    const index = itemRow.index ?? -1;
    if (!this.isItemIndexValid(index)) {
      return;
    }

    this.updateItems(
      (items) => {
        items.splice(index, 1);
        return items;
      },
      [itemRow],
      'delete',
    );
  }

  removeItems(itemIds: string[], source: ItemChangedEventSource = 'user'): void {
    const items = this._items();
    const itemRows: ItemRow[] = [];
    items.forEach((item, index) => {
      if (itemIds.includes(item.itemId)) {
        itemRows.push({ ...item, index });
      }
    });
    if (!itemRows.length) {
      return;
    }
    this.updateItems((items) => items.filter((item) => !itemIds.includes(item.itemId)), itemRows, 'delete', source);
  }

  removeItemsWithMissingDependencies(items: InventoryItemWithPricing[]): void {
    this.dependencyService
      .listMissingRequirementsForItems(
        this._config().partnerId,
        this._config().marketId,
        items,
        items.concat(this.activeItems()),
      )
      .pipe(
        map((missingRequirements) => Array.from(missingRequirements.keys())),
        take(1),
      )
      .subscribe((itemsMissingRequirements) => {
        this.removeItems(itemsMissingRequirements, 'dependency');
      });
  }

  addNewBillingTermForItemMatchingRow(itemRow: ItemRow, billingTermCallback: BillingTermCallbackFn): void {
    const index = itemRow.index ?? -1;
    if (!this.isItemIndexValid(index)) {
      return;
    }

    const itemCopy = structuredClone(itemRow);
    const indexForCopy = index + 1;
    billingTermCallback().subscribe((output) => {
      if (!output) {
        return;
      }

      if (!itemCopy.pricing) itemCopy.pricing = {};
      if (!itemCopy.pricing.billingPeriod) itemCopy.pricing.billingPeriod = {};
      itemCopy.pricing.billingPeriod.startDate = output.startDate;
      itemCopy.pricing.billingPeriod.endDate = output.endDate ?? undefined;
      itemCopy.pricing.billingPeriod.duration = output.duration ?? undefined;
      itemCopy.pricing.nonBillable = output.nonBillable;

      this.addItemAtIndex(itemCopy, indexForCopy);

      if (output.addAnotherTerm) {
        const itemRowCopy = structuredClone(itemRow);
        itemRowCopy.index = indexForCopy;
        this.addNewBillingTermForItemMatchingRow(itemRowCopy, billingTermCallback);
      }
    });
  }

  editBillingTermForItemMatchingRow(itemRow: ItemRow, billingTermCallback: BillingTermCallbackFn): void {
    const index = itemRow.index ?? -1;
    if (!this.isItemIndexValid(index)) {
      return;
    }

    const item = this.getItemWithIndex(index);
    billingTermCallback(true).subscribe((output) => {
      if (!output) {
        return;
      }

      this.setItemMatchingRow({
        ...itemRow,
        pricing: {
          ...item.pricing,
          billingPeriod: {
            startDate: output.startDate,
            endDate: output.endDate ?? undefined,
            duration: output.duration ?? undefined,
          },
          nonBillable: output.nonBillable,
        },
      });

      if (output.addAnotherTerm) {
        this.addNewBillingTermForItemMatchingRow(itemRow, billingTermCallback);
      }
    });
  }

  updateItemsWithPricing(itemRows: ItemRow[]): void {
    if (!itemRows?.length) {
      return;
    }
    const items = this._items();
    const changedRows: ItemRow[] = [];
    items.forEach((item, index) => {
      const itemRow = itemRows[index];
      const lineItem = CreateLineItemFromItemRow(itemRow, this._config().currencyOverride);
      if (!LineItemPricingEqual(item.pricing, lineItem)) {
        itemRow.pricing = lineItem;
        changedRows.push(itemRow);
      }
    });
    if (!changedRows.length) {
      return;
    }
    this.setItemsMatchingRows(changedRows);
  }

  updateItemsCurrency(currency: string): void {
    const items = this._items();
    const itemRows: ItemRow[] = [];
    items.forEach((item, index) => {
      const newItem = { ...item };
      if (newItem?.pricing?.currencyCode && newItem.pricing.currencyCode !== currency) {
        newItem.pricing.currencyCode = currency;
        itemRows.push({ ...newItem, index });
      }
    });
    if (!itemRows.length) {
      return;
    }
    return this.updateItems(
      (items) => {
        return items;
      },
      itemRows,
      'update',
    );
  }

  private isItemIndexValid(index: number): boolean {
    return index >= 0 && index < this._items().length;
  }

  private getItemWithIndex(index: number): ChangedInventoryItem {
    const item = this._items()[index];
    return {
      ...item,
      index,
    };
  }

  private updateItems(
    updateFn: UpdateItemsFn,
    changedItems: ItemRow[],
    eventType: ItemsChangedEventType,
    eventSource: ItemChangedEventSource = 'user',
  ): void {
    // Create a new array because change detection doesn't detect a change to an array's items, only the reference.
    this._items.update((items) => [...updateFn(items)]);
    this._events.update((events) => [...events, { items: changedItems, type: eventType, source: eventSource }]);
  }
}

type UpdateItemsFn = (items: ItemRow[]) => ItemRow[];
